import updateImageDoc from "./_updateImageDoc";
import { v4 as uuid } from "uuid";
import updateSingleFieldInstance from "./_updateSingleFieldInstance";
import { handleBatchCrud } from "./handleBatchCrud";
import batchCreateFieldInstances from "./batchCreateFieldInstances";
import batchCreateImages from "./batchCreateImages";
import { createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import { FieldInstance, FieldInstanceForResolver, FormGroup, FormInstance, Image } from "db/definitions";

interface BatchImageRollUploadData {
	config: { fieldToMatch: string; files: FileList | File[]; prefix: string };
	formGroup: FormGroup;
	formInstance: FormInstance;
}

/**
 * Handles uploading a batch of images into a photolog based on user selections and uploaded files.
 * @param {Object} data containing information for a batch upload to a photolog
 * @param {FormInstance} data.formInstance of the photolog that is going to be uploaded into
 * @param {Object} data.config of the selected properties for the batch upload and the files
 */
async function batchImageRollUpload(data: BatchImageRollUploadData, online: boolean): Promise<void> {
	const blankImageTemplate: Omit<Image, "id" | "key" | "createdAt" | "updatedAt"> = {
		dpi: "",
		rotation: "",
		aspectX: "",
		aspectY: "",
		group: data.formInstance.group,
		formGroup: data.formGroup.id,
		project: data.formGroup.projectId,
	};
	// Need to go through the instance find the image roll type field and loop through each row
	// to determine if the field template matches the data.config.fieldToMatch
	// As of right now each photo log template can only have one imageroll type field.
	const formInstance = data.formInstance;
	let imageRollField: FieldInstance | undefined = undefined;
	// This is a map to track each image field by the matched field's value.
	const imagesByMatchedValueObj: Record<string, Image> = {};
	const fieldInstancesToUpdateMap: Record<string, FieldInstance> = {};
	const instanceMap: Record<string, FieldInstance> = {};
	for (let i = 0; i < formInstance.fieldInstances.items.length; i++) {
		const fieldInstance = formInstance.fieldInstances.items[i];
		const type = fieldInstance.fieldTemplate?.inputType;

		if (type === "imageroll") {
			imageRollField = fieldInstance;
			if (imageRollField.fieldsOrder) {
				// we got the image roll, now time to attempt to match the cnfig id with a template/field
				// First up add all the table fields to an instance map so we can easily work with it
				for (let j = 0; j < fieldInstance.tableFields.items.length; j++) {
					const fieldInstanceInTable = fieldInstance.tableFields.items[j];
					instanceMap[fieldInstanceInTable.id] = fieldInstanceInTable;
				}

				// now go through each row in the fieldsOrder and split it
				for (let j = 0; j < imageRollField.fieldsOrder.length; j++) {
					const orderedIdString = imageRollField.fieldsOrder[j];
					const orderedRowIds = orderedIdString.split(",");
					let valueToMatch;
					let imageFieldInstance;

					// go through each id so we can make sure to check the whole row for
					// valueToMatch and the image field. Essentially orderedRowIds is looping
					// through an individual row.  We basically are matching a value from one field
					// to an image in another field in the same row.
					for (let s = 0; s < orderedRowIds.length; s++) {
						const orderedId = orderedRowIds[s];
						const fieldInstanceInTable = instanceMap[orderedId];
						if (fieldInstanceInTable.fieldTemplate) {
							const templateId = fieldInstanceInTable.fieldTemplate.id;
							if (templateId === data.config.fieldToMatch) {
								// got the correct field to match the image!
								valueToMatch = fieldInstanceInTable.value;
								continue;
							}

							if (fieldInstanceInTable.fieldTemplate.inputType === "image") {
								if (fieldInstanceInTable.image) {
									imageFieldInstance = fieldInstanceInTable;
									continue;
								} else {
									console.error("UHHHHHHH! theres an image type field instance without an image doc ", fieldInstanceInTable);
								}
							}
						}
					}

					if (valueToMatch && imageFieldInstance?.image) {
						imagesByMatchedValueObj[valueToMatch] = imageFieldInstance.image;
						fieldInstancesToUpdateMap[valueToMatch] = imageFieldInstance;
					}
				}
			}
		}
	}

	const imageFieldsToPutPromises = [];
	const imagesForNewRows = [];
	const fieldInstancesToCreate = [];
	const fieldInstancesToUpdate = [];
	// now time to loop through the files and using the trimmed filename, match to an image field.
	for (let i = 0; i < data.config.files.length; i++) {
		// Then need to parse out each of those field instances values to remove the data.config.prefix string and
		// match based on the file name vs. the remaining string/number
		const file = data.config.files[i];
		const trimmedFilename = file.name.replace(data.config.prefix, "");
		const trimmedNoExt = trimmedFilename.replace(/\.[^/.]+$/, "");
		// For each one of those need to send to be compressed then upload the compressed webp response.
		// Each one's response from the upload will then provide a key that needs to be set for each image field type
		// in the row.  We can use hand0leImageDoc with the last optional arg as true to handle the compression
		// and actual upload itself.. maybe
		if (imagesByMatchedValueObj[trimmedNoExt]) {
			// the image was matched to a field successfully - existing
			const oldImageDoc = imagesByMatchedValueObj[trimmedNoExt];

			const newImageDoc = {
				...oldImageDoc,
				key: {
					files: [file],
				},
			};

			// existing should already have an image id attached so don't need to update the image field
			imageFieldsToPutPromises.push(updateImageDoc(oldImageDoc, newImageDoc, data.formInstance.group, online ? "UP" : "DOWN", online, false, true));

			if (fieldInstancesToUpdateMap[trimmedNoExt]) {
				const instance = fieldInstancesToUpdateMap[trimmedNoExt];
				const instanceToUpdate = createFieldForBatchResolver(instance);
				fieldInstancesToUpdate.push(instanceToUpdate);
			}
		} else {
			// the image does not have a match in the photolog this needs to be separated to be created as
			// a new row

			// First make an "old image doc" for the update image doc module, at this point we will generate an
			// id
			const timestamp = new Date().toISOString();
			const oldImageDoc: Image = {
				...blankImageTemplate,
				id: uuid(),
				key: "",
				createdAt: timestamp,
				updatedAt: timestamp,
			};

			const newImageDoc = {
				...oldImageDoc,
				key: {
					files: [file],
				},
			};
			imageFieldsToPutPromises.push(updateImageDoc(oldImageDoc, newImageDoc, data.formInstance.group, online ? "UP" : "DOWN", online, false, true));
			// for a new one, we need to add the id to be added to the image field that will be created later
			// this thing needs the image doc id, and the parsed trimmed name (for inserting into the appropriate field)
			const newImageObject = {
				id: oldImageDoc.id,
				name: trimmedNoExt,
			};
			imagesForNewRows.push(newImageObject);
		}
	}

	const imageDocsToUpload = await Promise.all(imageFieldsToPutPromises);
	// Ok now to create new rows for unmatched images using the array of image document ids.
	if (imageRollField && imageRollField.fieldsOrder && imageRollField.fieldTemplate?.tableFields && imageRollField.fieldTemplate.rowOrder) {
		const rowTemplates = imageRollField.fieldTemplate?.tableFields?.items;
		const fieldsOrder = imageRollField.fieldsOrder;

		while (imagesForNewRows.length > 0) {
			let newFieldsOrderArray = [];
			const imageObject = imagesForNewRows.shift()!;
			for (let i = 0; i < rowTemplates.length; i++) {
				const template = rowTemplates[i];
				const newId = uuid();
				const templateOrder = imageRollField.fieldTemplate.rowOrder.indexOf(template.id);
				newFieldsOrderArray[templateOrder] = newId;
				const timeStamp = new Date().toISOString();
				const newInstance: FieldInstanceForResolver = {
					id: newId,
					authors: [],
					fieldTemplateId: template.id,
					formGroup: imageRollField.formGroup,
					group: imageRollField.group,
					tableField: imageRollField.id,
					value: "",
					notes: "",
					editing: "",
					createdAt: timeStamp,
					updatedAt: timeStamp,
					fieldsOrder: null,
				};

				if (template.id === data.config.fieldToMatch) {
					// This was the field intended to match, so we can autopopulate it with the trimmed image number.
					// which is just name prop on the imageObject
					newInstance.value = imageObject.name;
				}
				// set the new instance image id to one of the images created that didnt match earlier
				// using shift also mutates the array so this should work...
				if (template.inputType === "image") {
					newInstance.imageId = imageObject.id;
				}
				const finalInstance = createFieldForBatchResolver(newInstance);

				// put it in the batch to create
				fieldInstancesToCreate.push(finalInstance);
			}

			// each string needs to have the fieldinstances ordered, then just pushed into the array
			const fieldsOrderString = newFieldsOrderArray.join(",");
			fieldsOrder.push(fieldsOrderString);
		}

		// build out your document for the image roll field
		const updatedImageRollFieldInstanceDoc: FieldInstanceForResolver = {
			authors: imageRollField.authors,
			fieldTemplateId: imageRollField.fieldTemplateId,
			formGroup: imageRollField.formGroup,
			formInstance: imageRollField.formInstance,
			group: imageRollField.group,
			id: imageRollField.id,
			value: imageRollField.value,
			forceUpdateData: imageRollField.forceUpdateData,
			createdAt: imageRollField.createdAt,
			updatedAt: new Date().toISOString(),
			fieldsOrder,
			editing: imageRollField.editing || "",
			notes: "",
		};

		let finalFieldInstances = fieldInstancesToCreate;
		if (fieldInstancesToUpdate.length > 0 || imagesForNewRows.length > 0) {
			console.log(`field instances to update for batch photolog magic: `, fieldInstancesToUpdate);
			// There are image field instances that need to be updated so the forceUpdateData field needs to be set.
			updatedImageRollFieldInstanceDoc.forceUpdateData = `${Date.now()}-tableFields`;
			finalFieldInstances = fieldInstancesToCreate.concat(fieldInstancesToUpdate);
		}

		await Promise.all([
			// batch create(PUT) the image docs
			// this guy actually uses a put so handles updates and new ones
			handleBatchCrud(imageDocsToUpload, batchCreateImages, 25),
			// actually batchCreate(PUT) the field instance documents in the db
			// We do this before updating the imageroll field because that should trigger a realtime update
			// and these need to exist first.
			handleBatchCrud(finalFieldInstances, batchCreateFieldInstances, 25),
		]);
		// at one point this was to just include this doc in the batch update, but then the sub doesnt pick up on it
		// now it will be handled on its own
		await updateSingleFieldInstance(updatedImageRollFieldInstanceDoc);
	}
}

export default batchImageRollUpload;
